<script setup lang="ts">
import type { FallbackCategory } from '@game/types'

withDefaults(defineProps<{
    maxSections?: number
    hasSidebar?: boolean
    showAllLink?: boolean
    showAllLinkUrl?: string
    /** Categories rendered when personalized sections are unavailable. */
    fallbackCategories?: FallbackCategory[]
}>(), {
    hasSidebar: false,
    showAllLink: false,
    fallbackCategories: () => ['SLOTS'],
})

// `useLayoutContext` is provided by the dk/uk layer packages, not core itself.
// Resolved via the consumer's auto-imports at build time.
const { fetchPersonalizedContent, personalizedContentVisible } = useLayoutContext()
const { t } = useI18n()
const route = useRoute()

const pageRouteName = route.name
const pageSlug = shallowRef(route.meta?.pageSlug)
const showPersonalized = shallowRef(personalizedContentVisible.value)
const fetchPersonalized = shallowRef(fetchPersonalizedContent.value)

watch([
    () => route.meta?.pageSlug,
    personalizedContentVisible,
    fetchPersonalizedContent,
], ([slug, visible, enabled]) => {
    // Keep this page's content while the destination route updates before unmount.
    if (route.name !== pageRouteName) return

    pageSlug.value = slug
    showPersonalized.value = visible
    fetchPersonalized.value = enabled
})

const gameSectionsQuery = useGameSections({
    lazy: true,
    enabled: fetchPersonalized,
    pageSlug,
})
const sections = gameSectionsQuery.data
const isLoading = gameSectionsQuery.isLoading
</script>

<template>
    <div>
        <GameListDynamicSections
            v-if="showPersonalized"
            :sections="sections"
            :is-loading="isLoading"
            :max-sections="maxSections"
            :has-sidebar="hasSidebar"
        >
            <template #after-first>
                <slot name="after-first" />
            </template>
            <template #after-all>
                <slot name="after-all" />
            </template>
        </GameListDynamicSections>

        <div v-else class="personalized-sections__fallback">
            <GameListFallbackSection
                v-if="fallbackCategories[0]"
                :category="fallbackCategories[0]"
                :has-sidebar="hasSidebar"
                :lazy="false"
            />

            <slot name="after-first" />

            <LazyVisibleGameListFallbackSection
                v-for="category in fallbackCategories.slice(1)"
                :key="category"
                :category="category"
                hydrate-on-visible
            />

            <slot name="after-all" />
        </div>

        <div v-if="showAllLink && showAllLinkUrl" class="personalized-sections__show-all">
            <hr>
            <UIButton color="secondary" :to="showAllLinkUrl" glitch>
                <span v-text="t('game.gameList.show_all')" />
            </UIButton>
            <hr>
        </div>
    </div>
</template>

<style lang="scss" scoped>
// Same spacing as .dynamic-sections so fallback matches the personalized layout
// (DynamicSections' styles are scoped and can't be shared).
.personalized-sections__fallback {
    display: flex;
    flex-direction: column;
    gap: 20px;

    @include tablet(md) {
        gap: 30px;
    }

    // Gap handles all spacing; prevent double-spacing from GameList's global sibling margins.
    :deep(.custom-game-section + .custom-game-section) {
        margin-top: 0;
    }

    & > :deep(.game-list__section) {
        margin-top: 0;
    }
}

.personalized-sections__show-all {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-top: 20px;

    @include tablet(md) {
        margin-top: 30px;
    }

    hr {
        flex: 1;
        border: none;
        border-top: 1px solid var(--border-surface, $gray-lighter);
    }
}
</style>
